import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { createTRPCClient, httpBatchLink } from '@trpc/client'
import { type JSX, useState } from 'react'
import { z } from 'zod'
import { OrganizationAvatar } from '~/features/avatar'
import { getConfigValue } from '~/helpers/envHelpers'
import { getAuthToken } from '~/helpers/sessionHelpers'
import { useTRPC } from '~/services/trpc'
import { PublicTemplate } from '~/templates/public'

import { Alert, Button, Heading1, Spinner } from '@hap/components'

import type { AppRouter } from '../../../../../../packages/trpc/client-types'

export const Route = createFileRoute('/_authenticated/oauth/authorize')({
  validateSearch: z.object({
    client_id: z.string(),
    redirect_uri: z.string(),
    code_challenge: z.string(),
    scope: z.string().optional(),
    state: z.string().optional(),
    resource: z.string().optional(),
    client_name: z.string().optional(),
  }),
  component: RouteComponent,
})

type Organization = {
  id: string
  legacyId: string
  alias: string
  name: string
}

function RouteComponent(): JSX.Element {
  const trpc = useTRPC()
  const search = Route.useSearch()
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null)
  const [isApproving, setIsApproving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data: organizations, isLoading } = useQuery(
    trpc.organizations.getAll.queryOptions(),
  )

  const clientName = search.client_name || 'An MCP client'
  const activeOrg =
    selectedOrg ?? (organizations?.length === 1 ? organizations[0] : null)

  const approve = async (organization: Organization) => {
    setIsApproving(true)
    setError(null)
    try {
      // One-off client: the global tRPC client resolves org headers from the
      // URL, but this page isn't org-scoped — pass the chosen org explicitly.
      const client = createTRPCClient<AppRouter>({
        links: [
          httpBatchLink({
            url: getConfigValue('VITE_TRPC_URL'),
            methodOverride: 'POST',
            headers: {
              authorization: `Bearer ${getAuthToken()}`,
              'happitu-organization-id': organization.legacyId,
              'happitu-organization-alias': organization.alias,
            },
          }),
        ],
      })

      const { redirectTo } = await client.oauth.approve.mutate({
        clientId: search.client_id,
        redirectUri: search.redirect_uri,
        codeChallenge: search.code_challenge,
        scope: search.scope,
        state: search.state,
        resource: search.resource,
      })

      window.location.replace(redirectTo)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Something went wrong. Please try again.',
      )
      setIsApproving(false)
    }
  }

  const cancel = () => {
    try {
      const url = new URL(search.redirect_uri)
      url.searchParams.set('error', 'access_denied')
      url.searchParams.set('error_description', 'The user denied the request')
      if (search.state) url.searchParams.set('state', search.state)
      window.location.replace(url.toString())
    } catch {
      window.location.replace('/')
    }
  }

  if (isLoading) {
    return (
      <Spinner className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
    )
  }

  return (
    <PublicTemplate width={480}>
      <section className="bg-accent grid gap-8 p-8">
        <header className="grid gap-2 text-center">
          <Heading1>Connect to Happitu</Heading1>
          <p className="text-accent-foreground">
            <strong className="font-semibold">{clientName}</strong> is requesting
            read-only access to interaction insights
            {activeOrg ? (
              <>
                {' '}
                in <strong className="font-semibold">{activeOrg.name}</strong>
              </>
            ) : null}
            .
          </p>
        </header>

        {error && <Alert variant="warn">{error}</Alert>}

        {!organizations?.length ? (
          <Alert variant="warn">
            Your user currently has no access to any organizations.
          </Alert>
        ) : activeOrg ? (
          <div className="grid gap-2">
            <Button
              size="lg"
              loading={isApproving}
              loadingLabel="Connecting..."
              onClick={() => approve(activeOrg)}
            >
              Allow Access
            </Button>
            <Button variant="secondary" size="lg" onClick={cancel}>
              Cancel
            </Button>
            {organizations.length > 1 && (
              <Button variant="ghost" size="sm" onClick={() => setSelectedOrg(null)}>
                Choose a different organization
              </Button>
            )}
          </div>
        ) : (
          <div className="grid gap-2">
            <p className="text-accent-foreground text-center">
              Choose which organization to connect:
            </p>
            {organizations.map((org) => (
              <a
                key={org.id}
                className="bg-background group flex cursor-pointer items-center gap-4 p-4 transition-transform hover:scale-102 hover:shadow-md"
                onClick={() => setSelectedOrg(org)}
              >
                <OrganizationAvatar name={org.name} />
                <div className="grow">{org.name}</div>
              </a>
            ))}
            <Button variant="secondary" size="lg" onClick={cancel}>
              Cancel
            </Button>
          </div>
        )}
      </section>
    </PublicTemplate>
  )
}
